import { useEffect, useMemo, useState, useTransition } from 'react';
import { Global } from '@emotion/react';
import { Modal } from 'antd';
import { CloseOutlined } from '@ant-design/icons';

import AxisConfigurationModalFooter from 'components/molecules/PackArchitecture/AxisConfigurationModalFooter';
import { getConfigurationTitle } from 'components/molecules/PackArchitecture/axisConfigurationModal.constants';
import {
    AXIS_CONFIGURATION_MODAL_CLASS,
    axisConfigurationModalGlobalStyles,
} from 'components/molecules/PackArchitecture/AxisConfigurationModal.styles';
import KpiAxisConfigurationModalConfigurationControls from 'components/molecules/PackArchitecture/KpiAxisConfigurationModalConfigurationControls';
import KpiAxisConfigurationModalPreview from 'components/molecules/PackArchitecture/KpiAxisConfigurationModalPreview';
import {
    type KpiAxisConfiguration,
    type KpiBinLabelAffixes,
    createDefaultKpiAxisConfiguration,
    kpiConfigsAreEqual,
    type KpiDataRange,
} from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import { DEFAULT_KPI_DATA_RANGE } from 'constants/ppa';
import type { KpiAxisControlsMeta } from 'components/molecules/PackArchitecture/kpiAxisControlsMeta.types';
import { getKpiAxisConfigurationDefaultsFromMeta } from 'components/pages/PackArchitecture/packArchitectureMeta.helpers';

export type { KpiAxisConfiguration, KpiDataRange } from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';

type KpiAxisConfigurationModalProps = {
    open: boolean;
    kpiLabel: string;
    controlsMeta?: KpiAxisControlsMeta | null;
    initialConfig?: KpiAxisConfiguration;
    dataRange?: KpiDataRange;
    binLabelAffixes?: KpiBinLabelAffixes;
    onClose: () => void;
    onApply?: (config: KpiAxisConfiguration) => void;
};

const KpiAxisConfigurationModal = ({
    open,
    kpiLabel,
    controlsMeta,
    initialConfig,
    dataRange = DEFAULT_KPI_DATA_RANGE,
    binLabelAffixes,
    onClose,
    onApply,
}: KpiAxisConfigurationModalProps) => {
    const configurationDefaults = useMemo(
        () => getKpiAxisConfigurationDefaultsFromMeta(controlsMeta),
        [controlsMeta],
    );
    const appliedConfig = useMemo(
        () => initialConfig ?? createDefaultKpiAxisConfiguration(dataRange, configurationDefaults),
        [configurationDefaults, dataRange, initialConfig],
    );
    const defaultConfig = useMemo(
        () => createDefaultKpiAxisConfiguration(dataRange, configurationDefaults),
        [configurationDefaults, dataRange],
    );

    const [config, setConfig] = useState<KpiAxisConfiguration>(appliedConfig);
    const [baselineConfig, setBaselineConfig] = useState<KpiAxisConfiguration>(appliedConfig);
    const [isPreviewPending, startPreviewTransition] = useTransition();

    useEffect(() => {
        if (open) {
            setConfig(appliedConfig);
            setBaselineConfig(appliedConfig);
        }
    }, [open, appliedConfig]);

    const hasApplyChanges = useMemo(
        () => !kpiConfigsAreEqual(config, baselineConfig),
        [baselineConfig, config],
    );

    const hasResetChanges = useMemo(
        () => !kpiConfigsAreEqual(config, defaultConfig),
        [config, defaultConfig],
    );

    const updateConfig = (patch: Partial<KpiAxisConfiguration>) => {
        startPreviewTransition(() => {
            setConfig((current) => ({ ...current, ...patch }));
        });
    };

    const handleReset = () => {
        setConfig(defaultConfig);
    };

    const handleCancel = () => {
        setConfig(baselineConfig);
        onClose();
    };

    const handleApply = () => {
        setBaselineConfig(config);
        onApply?.(config);
        onClose();
    };

    return (
        <>
            <Global styles={axisConfigurationModalGlobalStyles} />
            <Modal
                title={getConfigurationTitle(kpiLabel)}
                open={open}
                onCancel={handleCancel}
                closeIcon={<CloseOutlined className="text-sm text-toolbarLabelGrey" />}
                width={720}
                footer={
                    <AxisConfigurationModalFooter
                        hasApplyChanges={hasApplyChanges}
                        hasResetChanges={hasResetChanges}
                        onReset={handleReset}
                        onCancel={handleCancel}
                        onApply={handleApply}
                    />
                }
                destroyOnClose
                centered
                wrapClassName={AXIS_CONFIGURATION_MODAL_CLASS}
            >
                <div className="flex h-full min-h-0">
                    {controlsMeta ? (
                        <KpiAxisConfigurationModalConfigurationControls
                            config={config}
                            controlsMeta={controlsMeta}
                            dataRange={dataRange}
                            defaultBinCount={configurationDefaults?.binCount ?? controlsMeta.bins.uniform.count.defaultValue}
                            binLabelAffixes={binLabelAffixes}
                            onChange={updateConfig}
                        />
                    ) : null}
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-axisModalBg pl-6 pt-4">
                        <KpiAxisConfigurationModalPreview
                            config={config}
                            binLabelAffixes={binLabelAffixes}
                            isLoading={isPreviewPending}
                        />
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default KpiAxisConfigurationModal;
